import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuProps,
  styled,
  Switch,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { AnchorHTMLAttributes, InputHTMLAttributes } from 'react';

import { doNotForwardTheseProps } from '@/styles';

import { Icon } from '../Icon/Icon';
import { IconProps } from '../Icon/Icon.interface';

export const FullHeightTextFieldContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.neutral[200],
  borderColor: theme.palette.neutral[200],
  borderStyle: 'solid',
  borderRadius: theme.shape.borderRadius,
  borderWidth: 1,
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  transition: theme.transitions.create('border-color'),

  ':hover': {
    borderColor: theme.palette.neutral[1000],
  },

  ':has(> textarea:focus)': {
    borderColor: theme.palette.neutral[1000],
    outline: 'none',
  },
}));

export const FullHeightTextarea = styled('textarea', {
  shouldForwardProp: doNotForwardTheseProps('value'),
})(({ theme }) => ({
  ...theme.typography.body2,
  backgroundColor: 'transparent',
  border: 'none',
  resize: 'none',
  width: '100%',
  height: '100%',
  paddingBottom: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),

  ':focus': {
    outline: 'none',
  },
}));

export const StyledSwitch = styled(Switch)(({ theme }) => ({
  borderRadius: 86,
  width: 56,
  height: 32,
  padding: 0,

  '.MuiSwitch-switchBase': {
    padding: 4,

    ':hover': {
      backgroundColor: 'transparent',
    },

    '+ .MuiSwitch-track': {
      backgroundColor: theme.palette.neutral[300],
      opacity: 1,
    },
    '.MuiSwitch-thumb': {
      boxShadow: 'none',
      height: 24,
      width: 24,
    },

    '&.Mui-checked': {
      transform: 'translateX(24px)',

      '.MuiSwitch-thumb': {
        backgroundColor: theme.palette.common.white,
      },
      '+ .MuiSwitch-track': {
        backgroundColor: theme.palette.primaries[400],
        opacity: 1,
      },
    },
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    pointerEvents: 'none',
    display: 'inline-flex',
  },
}));

export const SearchTextField = styled(TextField)(({ theme }) => ({
  '.MuiInputBase-root': {
    '&:not(.MuiInputBase-multiline)': {
      height: 36,
    },

    backgroundColor: theme.palette.neutral[200],
    borderRadius: 10,
    paddingLeft: theme.spacing(1),

    '.MuiOutlinedInput-notchedOutline': {
      display: 'none',
    },

    '.MuiInputBase-input': {
      paddingLeft: 0,
    },
  },
}));

export const FilterMenu = styled((props: MenuProps) => (
  <Menu
    slotProps={{
      paper: {
        sx: theme => ({
          padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
          '.MuiList-root': {
            padding: 0,
          },
        }),
      },
    }}
    {...props}
  />
))(() => ({}));

export const CheckboxContainer = styled('div', {
  shouldForwardProp: doNotForwardTheseProps('checked'),
})<{ checked?: boolean }>(({ theme, checked }) => ({
  height: 32,
  width: 32,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: 4,

  '.MuiSvgIcon-root': {
    fill: theme.palette.common.white,
  },

  ...(checked
    ? {
        backgroundColor: theme.palette.primaries[400],
      }
    : {
        backgroundColor: theme.palette.common.white,
        boxShadow: `inset 0 0 0 2px ${theme.palette.neutral[600]}`,
      }),
}));

export const StyledCheckboxFormControlLabel = styled(FormControlLabel)(() => ({
  margin: 0,
  '.MuiTypography-root': {
    userSelect: 'none',
  },
}));

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 0,
  marginRight: theme.spacing(1.5),
}));

export const VisuallyHiddenFileInput = styled(
  (props: InputHTMLAttributes<HTMLInputElement>) => (
    <input type="file" {...props} />
  ),
)<InputHTMLAttributes<HTMLInputElement>>({
  opacity: 0,
  cursor: 'pointer',
  position: 'absolute',
  bottom: 0,
  left: 0,
  '::file-selector-button': {
    display: 'none',
  },
});

export const StyledDatePicker = styled(DatePicker)<DatePickerProps<Date>>(
  ({ theme }) => ({
    // TODO: update this when MUI v7 is released
    // '.MuiPickersInputBase-root': {
    //   paddingRight: theme.spacing(0.5),
    //   '.MuiPickersSectionList-root': {
    //     padding: `${theme.spacing(1.3125)} 0`,
    //   },
    //   '.MuiPickersInputBase-input': {
    //     padding: 0,
    //   },
    // },
    '.MuiInputBase-root': {
      paddingRight: theme.spacing(0.5),
      '.MuiInputBase-input': {
        padding: 0,
      },
    },
  }),
);

export const FieldArrowButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1),
  ':hover': {
    backgroundColor: 'transparent',
  },

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(0.5),
  },
}));

export const FieldArrow = styled((props: IconProps) => (
  <Icon
    size={20}
    color={(theme: Theme) => theme.palette.neutral[700]}
    {...props}
  />
))(({ name }) => ({
  marginLeft: 'auto',
  ...(name === 'arrow-up'
    ? {
        transform: `translateY(-2px)`,
      }
    : {}),
}));

export const Hyperlink = styled(
  (props: AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a target="_blank" rel="noopener noreferrer" {...props} />
  ),
)(({ theme }) => ({
  color: theme.palette.primaries[500],
  fontSize: theme.typography.captionBold.fontSize,
  fontWeight: theme.typography.captionBold.fontWeight,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const StyledLoadingButton = styled(Button, {
  shouldForwardProp: doNotForwardTheseProps('isLoading'),
})<{ isLoading?: boolean }>(({ isLoading, variant, theme }) => ({
  position: 'relative',
  ...(isLoading
    ? {
        color: 'transparent',
        '.MuiButton-startIcon, .MuiButton-endIcon': {
          '.MuiSvgIcon-root': {
            fill: 'transparent',
          },
        },
        '.MuiCircularProgress-root': {
          color:
            variant === 'contained'
              ? theme.palette.common.white
              : theme.palette.text.primary,
        },

        '&.Mui-disabled': {
          color: 'transparent',
          '.MuiCircularProgress-root': {
            color: theme.palette.neutral[400],
          },
        },
      }
    : {}),
}));

export const ButtonLoaderWrapper = styled(Box)(() => ({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const InfoReviewFieldLabel = styled((props: TypographyProps) => (
  <Typography variant="captionC1Regular" {...props} />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
  color: theme.palette.neutral[700],
}));

export const InfoReviewFieldValue = styled((props: TypographyProps) => (
  <Typography variant="bodyB2Medium" {...props} />
))(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));
